/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { DateTime, TimePlugin, easepick } from '@easepick/bundle';
import clsx from 'clsx';
import React, { useEffect, useMemo } from 'react';
import DatePicker, { RangePicker } from 'react-ease-picker';
import { ICON_EVENT } from '../../Icons';
import { SdsIcon } from '../SdsIcon';
import { PICKER_DATE_FORMAT, PICKER_RANGE_DELIMITER, PICKER_TIME_FORMAT } from './DatetimePicker.constants';
import { DatetimePickerProps, datetimePickerDefaults } from './DatetimePicker.props';
import { rangePickerCss } from './DatetimePicker.styles';

/**
 * SDS DatetimePicker component.
 */
export const DatetimePicker = (props: DatetimePickerProps): JSX.Element => {
  const {
    callback,
    clearCallback,
    classes,
    dateMaximum,
    dateMinimum,
    disabled,
    format,
    placeholder,
    presetEndDatetime,
    presetStartDatetime,
    selectedDate,
    type,
  } = {
    ...datetimePickerDefaults,
    ...props,
  };

  // Set up selectable date range if provided
  const minDateString = dateMinimum ? dateMinimum.format(PICKER_DATE_FORMAT) : undefined;
  const maxDateString = dateMaximum ? dateMaximum.format(PICKER_DATE_FORMAT) : undefined;

  // Set up preselected dates if provided
  const startDate = presetStartDatetime ? presetStartDatetime.toString() : undefined;
  const endDate = presetEndDatetime ? presetEndDatetime.toString() : undefined;
  const requireTimePlugin = /hh|HH|mm|ss|a|A/.test(format);

  // Get times from select fields
  const getPickerTimes = (timeContainer: HTMLDivElement, type: string): Array<number> => {
    const hoursSelect = timeContainer.querySelector(`select[name="${type}[HH]"]`) as HTMLSelectElement;
    const minutesSelect = timeContainer.querySelector(`select[name="${type}[mm]"]`) as HTMLSelectElement;
    const secondsSelect = timeContainer.querySelector(`select[name="${type}[ss]"]`) as HTMLSelectElement;

    return [
      parseInt(hoursSelect.value),
      parseInt(minutesSelect.value),
      secondsSelect ? parseInt(secondsSelect.value) : 0,
    ];
  };

  // Set picker start and end times
  const setPickerStartEndTimes = (picker: easepick.Core, pickedStart: DateTime, pickedEnd: DateTime): void => {
    // Times are in the provided select fields in the picker
    // but do not have an accessible API, so we need to query.
    const timeContainer = picker.ui.container.querySelector(`.time-plugin-container`) as HTMLDivElement;

    // Add times to selected date
    if (requireTimePlugin) {
      const startTimes = getPickerTimes(timeContainer, `start`);
      pickedStart.setHours(startTimes[0], startTimes[1], startTimes[2]);

      const endTimes = getPickerTimes(timeContainer, `end`);
      pickedEnd.setHours(endTimes[0], endTimes[1], endTimes[2]);

      // Refresh the display
      picker.setStartDate(pickedStart);
      picker.setEndDate(pickedEnd);
    }
    // Send result back
    callback({
      endDateTime: pickedEnd.toJSDate().getTime(),
      startDateTime: pickedStart.toJSDate().getTime(),
    });
  };

  const setSinglePickerDate = (picker: easepick.Core, date: DateTime): void => {
    const timeContainer = picker.ui.container.querySelector(`.time-plugin-container`) as HTMLDivElement;
    if (requireTimePlugin) {
      const pickerTime = getPickerTimes(timeContainer, `input`);
      date.setHours(pickerTime[0], pickerTime[1]);
      picker.setDate(date);
    }

    callback({
      date: date.toJSDate().getTime(),
    });
  };

  // Manually set input to disabled if specified in props, as Easepick doesn't support it.
  useEffect(() => {
    const pickersToDisable = document.querySelectorAll('.sds-datetime-picker--disabled');
    if (disabled && pickersToDisable.length) {
      pickersToDisable.forEach((picker) => {
        picker.setAttribute('disabled', `true`);
      });
    }
    return (): void =>
      pickersToDisable.forEach((picker) => {
        picker.setAttribute('disabled', `false`);
      });
  }, [disabled]);

  const basePickerOptions = {
    css: rangePickerCss,
    ...(requireTimePlugin && {
      plugins: [TimePlugin],
      TimePlugin: {
        stepMinutes: 1,
        stepSeconds: 1,
      },
    }),
  };

  const basePickerProps = {
    maxDate: maxDateString,
    minDate: minDateString,
    onSelect: /* istanbul ignore next */ (): void => {
      // Override onSelect with options setup function to access the picker object.
      // This required property remains empty and this code is not reachable.
    },
    placeholder: placeholder,
  };

  // Wrap this in useMemo to prevent re-render when using callback
  const createDatetimePicker = useMemo((): JSX.Element => {
    if (type === 'SINGLE') {
      return (
        <DatePicker
          {...basePickerProps}
          className={clsx(`sds-datetime-picker sds-datetime-picker--single`, {
            [`sds-datetime-picker--disabled`]: disabled,
          })}
          // Prop selectedDate must be in format of string timestamp, otherwise it might cause errors - use new Date($your_date).getTime().toString() to make it work correctly.
          date={selectedDate}
          format={format}
          options={{
            ...basePickerOptions,
            setup(picker): void {
              picker.on('render', () => {
                const SRDocument = picker.ui.shadowRoot.firstChild as HTMLDivElement;
                SRDocument.setAttribute('role', 'document');
                const pickerNext = picker.ui.container.querySelector('.next-button') as HTMLButtonElement;
                const pickerPrev = picker.ui.container.querySelector('.previous-button') as HTMLButtonElement;

                pickerNext.setAttribute('aria-label', 'Next month');
                pickerPrev.setAttribute('aria-label', 'Previous month');
                if (requireTimePlugin) {
                  const pickerHourSelect = picker.ui.container.querySelector('[name="input[HH]"]') as HTMLSelectElement;
                  const pickerMinuteSelect = picker.ui.container.querySelector(
                    '[name="input[mm]"]',
                  ) as HTMLSelectElement;
                  pickerHourSelect.setAttribute('aria-label', 'Hour');
                  pickerMinuteSelect.setAttribute('aria-label', 'Minutes');
                }
              });
              picker.on('show', () => {
                if (selectedDate) {
                  const date = picker.getDate().format(PICKER_TIME_FORMAT);
                  requireTimePlugin && picker.setTime(date);
                }
              });
              picker.on('select', (event) => {
                const date = event.detail.date as DateTime;
                setSinglePickerDate(picker, date);
              });
              picker.on('hide', () => {
                const date = picker.getDate();
                if (date) {
                  setSinglePickerDate(picker, date);
                }
              });
            },
            ...(requireTimePlugin && {
              TimePlugin: {
                ...basePickerOptions.TimePlugin,
                seconds: format.includes('ss'),
              },
            }),
          }}
        />
      );
    }

    return (
      <RangePicker
        {...basePickerProps}
        calendars={1}
        className={clsx('sds-datetime-picker sds-datetime-picker--range', {
          [`sds-datetime-picker--disabled`]: disabled,
        })}
        endDate={endDate}
        options={{
          ...basePickerOptions,
          format,
          RangePlugin: {
            delimiter: PICKER_RANGE_DELIMITER,
          },
          setup(picker): void {
            picker.on('clear', () => clearCallback && clearCallback());
            picker.on('render', () => {
              const SRDocument = picker.ui.shadowRoot.firstChild as HTMLDivElement;
              SRDocument.setAttribute('role', 'document');
              const pickerNext = picker.ui.container.querySelector('.next-button') as HTMLButtonElement;
              const pickerPrev = picker.ui.container.querySelector('.previous-button') as HTMLButtonElement;
              pickerNext.setAttribute('aria-label', 'Next month');
              pickerPrev.setAttribute('aria-label', 'Previous month');
              if (requireTimePlugin) {
                const pickerStartHourSelect = picker.ui.container.querySelector(
                  '[name="start[HH]"]',
                ) as HTMLSelectElement;
                const pickerStartMinuteSelect = picker.ui.container.querySelector(
                  '[name="start[mm]"]',
                ) as HTMLSelectElement;
                const pickerStartSecondsSelect = picker.ui.container.querySelector(
                  '[name="start[ss]"]',
                ) as HTMLSelectElement;
                const pickerEndHourSelect = picker.ui.container.querySelector('[name="end[HH]"]') as HTMLSelectElement;
                const pickerEndMinuteSelect = picker.ui.container.querySelector(
                  '[name="end[mm]"]',
                ) as HTMLSelectElement;
                const pickerEndSecondsSelect = picker.ui.container.querySelector(
                  '[name="end[ss]"]',
                ) as HTMLSelectElement;
                pickerStartHourSelect.setAttribute('aria-label', 'Start hour');
                pickerStartMinuteSelect.setAttribute('aria-label', 'Start minutes');
                pickerStartSecondsSelect?.setAttribute('aria-label', 'Start seconds');
                pickerEndHourSelect.setAttribute('aria-label', 'End hour');
                pickerEndMinuteSelect.setAttribute('aria-label', 'End minutes');
                pickerEndSecondsSelect?.setAttribute('aria-label', 'End seconds');
              }
            });
            picker.on('show', () => {
              // Get the start and end dates (if provided) to set the time
              if (startDate) {
                // optional chaining - guard for the case when picker.getStartDate() returns null
                // istanbul ignore next
                const startTime = picker.getStartDate()?.format(PICKER_TIME_FORMAT);
                if (requireTimePlugin) {
                  picker.setStartTime(startTime);
                }
              }

              if (endDate) {
                // optional chaining - guard for the case when picker.getEndDate() returns null
                // istanbul ignore next
                const endTime = picker.getEndDate()?.format(PICKER_TIME_FORMAT);
                if (requireTimePlugin) {
                  picker.setEndTime(endTime);
                }
              }
            });
            /* istanbul ignore next */
            picker.on('select', (event) => {
              // Selected dates are in the event.detail object
              const pickedStart = event.detail.start as DateTime;
              const pickedEnd = event.detail.end as DateTime;

              setPickerStartEndTimes(picker, pickedStart, pickedEnd);
            });
            picker.on('hide', () => {
              const pickedStart = picker.getStartDate();
              const pickedEnd = picker.getEndDate();

              // Hack to set the start and end times after selecting a dates
              if (pickedStart && pickedEnd) {
                setPickerStartEndTimes(picker, pickedStart, pickedEnd);
              }
            });
          },
          ...(requireTimePlugin && {
            TimePlugin: {
              ...basePickerOptions.TimePlugin,
              seconds: format.includes('ss'),
            },
          }),
        }}
        position="left"
        startDate={startDate}
      />
    );
    // Including startDate and endDate will break the memo and cause a re-render
    // due to the timestamps changing. Exclude them from the dependencies list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callback, format, maxDateString, minDateString, placeholder]);

  return (
    <div className={`sds-form-input sds-datetime-picker ${classes}`}>
      <div className="sds-text-field">
        <div className="sds-datetime-picker__icon">
          <SdsIcon svg={ICON_EVENT} />
        </div>
        {createDatetimePicker}
      </div>
    </div>
  );
};
